import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../api/client.ts";
import { confirmAction, toast } from "./feedback.tsx";
import { Drawer, Empty } from "./ui.tsx";

export interface MediaItem {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  bytes: number;
  createdAt: string;
  uploadedBy: string | null;
}

interface MediaList {
  /** False when the API has no S3 bucket, so uploading would only fail. */
  configured: boolean;
  items: MediaItem[];
}

function useMedia() {
  return useQuery({ queryKey: ["media"], queryFn: () => api.get<MediaList>("/media") });
}

/**
 * Multipart, so this bypasses the JSON client rather than fighting its
 * content-type — FormData sets its own boundary.
 */
async function uploadMedia(file: File): Promise<MediaItem> {
  const form = new FormData();
  form.set("file", file);
  const res = await fetch("/api/media", { method: "POST", body: form, credentials: "same-origin" });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? "upload failed");
  }
  return (await res.json()) as MediaItem;
}

function useUploadMedia(onUploaded?: (item: MediaItem) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadMedia,
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: ["media"] });
      onUploaded?.(item);
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
}

/** A file picker behind a button, for places that have no room for a dropzone. */
export function UploadButton({
  onUploaded,
  label = "Upload image",
  className = "btn ghost small",
}: {
  onUploaded?: ((item: MediaItem) => void) | undefined;
  label?: string;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadMedia(onUploaded);
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? "Uploading…" : label}
      </button>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The library as a grid of thumbnails. With `onPick` a click chooses an image;
 * without it each tile offers copy and delete, which is the Media page.
 */
export function MediaGrid({ onPick }: { onPick?: (item: MediaItem) => void }) {
  const qc = useQueryClient();
  const media = useMedia();
  const upload = useUploadMedia(onPick);
  const [dragging, setDragging] = useState(false);
  const [filter, setFilter] = useState("");

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/media/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["media"] });
      toast("Image deleted");
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });

  if (media.data && !media.data.configured) {
    return (
      <div className="notice warn">
        Image uploads aren&rsquo;t set up yet: the API needs an S3 bucket. Set{" "}
        <code>S3_BUCKET</code>, <code>S3_ACCESS_KEY_ID</code>, <code>S3_SECRET_ACCESS_KEY</code> and{" "}
        <code>MEDIA_PUBLIC_URL</code>, then redeploy.
      </div>
    );
  }

  const items = (media.data?.items ?? []).filter((m) =>
    m.filename.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the file input inside is the accessible control; dropping is a shortcut. */}
      <div
        className={`dropzone${dragging ? " over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          for (const file of e.dataTransfer.files) upload.mutate(file);
        }}
      >
        <p>
          {upload.isPending
            ? "Uploading…"
            : "Drop PNG, JPEG or GIF images here, up to 5MB. They're hosted publicly, so never upload anything private."}
        </p>
        <UploadButton className="btn small" label="Choose a file" onUploaded={onPick} />
      </div>

      {(media.data?.items.length ?? 0) > 6 && (
        <div className="toolbar" style={{ marginTop: 14, marginBottom: 0 }}>
          <input
            type="search"
            placeholder="Filter by name"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      )}

      {media.isLoading ? null : items.length === 0 ? (
        <Empty>{filter ? "Nothing matches." : "No images yet."}</Empty>
      ) : (
        <div className="media-grid">
          {items.map((m) => (
            <div className="media-tile" key={m.id}>
              {onPick ? (
                <button
                  type="button"
                  className="media-thumb"
                  title={`Use ${m.filename}`}
                  onClick={() => onPick(m)}
                >
                  <img src={m.url} alt={m.filename} loading="lazy" />
                </button>
              ) : (
                <a className="media-thumb" href={m.url} target="_blank" rel="noreferrer">
                  <img src={m.url} alt={m.filename} loading="lazy" />
                </a>
              )}
              <div className="media-meta">
                <div className="media-name" title={m.filename}>
                  {m.filename}
                </div>
                <div className="media-sub">
                  {formatBytes(m.bytes)} · {new Date(m.createdAt).toLocaleDateString()}
                  {m.uploadedBy ? ` · ${m.uploadedBy}` : ""}
                </div>
                {!onPick && (
                  <div className="media-actions">
                    <button
                      type="button"
                      className="btn ghost small"
                      onClick={() =>
                        void navigator.clipboard
                          .writeText(m.url)
                          .then(() => toast("URL copied"))
                          .catch(() => toast("Couldn't copy the URL", "bad"))
                      }
                    >
                      Copy URL
                    </button>
                    <button
                      type="button"
                      className="btn ghost small danger-text"
                      disabled={remove.isPending}
                      onClick={async () => {
                        if (
                          await confirmAction({
                            title: `Delete ${m.filename}?`,
                            message:
                              "Emails that have already gone out with this image will show it as broken.",
                            confirmLabel: "Delete image",
                            destructive: true,
                          })
                        )
                          remove.mutate(m.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function MediaPicker({
  onPick,
  onClose,
}: {
  onPick: (item: MediaItem) => void;
  onClose: () => void;
}) {
  return (
    <Drawer title="Choose an image" subtitle="Or upload a new one." onClose={onClose}>
      <MediaGrid
        onPick={(item) => {
          onPick(item);
          onClose();
        }}
      />
    </Drawer>
  );
}
